/**
 * RIG surface composition (v2 — Arrangement IA-2, Interpretation rail).
 *
 * Sealed IA-2 (BDSE_RIG_v2_Gate.html, ia2HTML): a FULL-WIDTH graph hero leads,
 * above a split. The left EVIDENCE column carries the relationship reads, then
 * cross-practice delivery. The right sticky INTERPRETATION rail carries the
 * selected-contact detail, then Pattern Read, then warm paths — so the reading
 * (who + what pattern + how to reach them) travels with the user as they click
 * through the graph and the reads.
 *
 * Permanent removals from v1 (gone, not disabled): the whole activation column —
 * account posture block, recommended action + talk track, outreach/ownership
 * action buttons (Signal-modal-duplicating mutations), pursuit ownership box,
 * pursuit stage tracker — plus the signal brief beyond the one-line entry-card
 * header and the full action history (reduced to a one-line recent whisper).
 * The contact modal remains the sole PII home.
 */

import { useCallback, useId, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type {
  RigContact,
  RigDelivery,
  RigPatternRead,
  RigViewModel,
  RigWarmPath,
} from "../types";
import { layoutGraph, type GNode } from "../layout";
import { RigGraph } from "./RigGraph";
import { RigContactModal } from "./RigContactModal";
import { BasisChip, RichTextView, TempPip } from "./parts";
import { TEMP_LABEL, roleSuffix } from "../logic";
import { signalNodeLabel } from "../signalNodeLabel";
import { isInertBackgroundClick, rigBackTarget } from "../back-navigation";

function EntryChips({ vm }: { vm: RigViewModel }): JSX.Element {
  return (
    <div className="rig-entry-chips">
      {vm.entry.chips.map((chip, i) => {
        if (chip.kind === "led") {
          return (
            <span key={i} className="rig-chip rig-solid-teal rig-mono">
              <span className="rig-led" />
              {chip.label}
            </span>
          );
        }
        const cls =
          chip.kind === "solid"
            ? "rig-chip rig-solid-teal rig-mono"
            : chip.kind === "value"
              ? "rig-chip rig-value rig-mono"
              : "rig-chip rig-mono";
        return (
          <span key={i} className={cls}>
            {chip.label}
          </span>
        );
      })}
    </div>
  );
}

function EntryCard({ vm, backTo }: { vm: RigViewModel; backTo: string | null }): JSX.Element {
  return (
    <section className="rig-entry">
      {/* Back to the originating signal (3B.4, strengthened under R-6/D). Same
          control, same top-left position — promoted from the 11.5px mini-link to
          the deployed rig-btn vocabulary so it carries a real hit target and
          visible weight. rig-btn is existing design-system styling (already used
          by the no-signal state); no new colour, shape, or fixed-position
          element. Kept as a Link, not a button, so middle-click and
          open-in-new-tab keep working — it shares its destination with the
          dead-space handler via rigBackTarget. */}
      {backTo ? (
        <Link
          to={backTo}
          className="rig-btn"
          style={{ marginBottom: 14, textDecoration: "none" }}
        >
          <span aria-hidden="true">←</span> Back to signal
        </Link>
      ) : null}
      <div className="rig-eyebrow">Relationship Intelligence Graph · Entry</div>
      <h1>{vm.entry.headline}</h1>
      <div className="rig-summary">
        <RichTextView value={vm.entry.summary} />
      </div>
      <EntryChips vm={vm} />
    </section>
  );
}

/** Pattern Read — the accretive "look around the corner" layer. Renders authored
 *  reads in the sealed hedged language, or an honest empty state when none are
 *  authored (live signals never show fabricated pattern claims). */
function PatternField({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rig-pread-field">
      <div className="rig-sec-eyebrow">{label}</div>
      <div className="rig-pread-body">{value}</div>
    </div>
  );
}

function PatternReadPanel({ reads }: { reads: RigPatternRead[] }): JSX.Element {
  return (
    <div className="rig-panel rig-pattern">
      <div className="rig-pattern-head">
        Pattern Read <span className="rig-pattern-corner">· look around the corner</span>
      </div>
      {reads.length === 0 ? (
        <div className="rig-pattern-empty">
          Pattern reads are authored per account — none yet for this signal.
        </div>
      ) : (
        <div className="rig-preads">
          {reads.map((p, i) => (
            <div className="rig-pread" key={i}>
              <div className="rig-pread-name">{p.patternName}</div>
              <PatternField label="What historically follows" value={p.expectedNextDevelopment} />
              <PatternField label="Why now" value={p.whyNow} />
              <PatternField label="Pursuit surface" value={p.pursuitSurface} />
              <PatternField label="Recommended next step" value={p.recommendedNextAction} />
              {p.watchFor.length > 0 ? (
                <div className="rig-pread-field">
                  <div className="rig-sec-eyebrow">Watch for</div>
                  <ul className="rig-pread-watch">
                    {p.watchFor.map((w, j) => (
                      <li key={j}>{w}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {p.evidence.requirement || p.evidence.confidenceLevel ? (
                <div className="rig-pread-field">
                  <div className="rig-sec-eyebrow">Evidence</div>
                  <div className="rig-pread-body">
                    {[p.evidence.requirement, p.evidence.confidenceLevel]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DeliveryRow({ d }: { d: RigDelivery }): JSX.Element {
  return (
    <div className="rig-del-row">
      <div className="rig-del-head">
        <span className="rig-prac">{d.practice}</span>
        {d.descriptor}
      </div>
      <div className="rig-del-meta">
        {d.valueTier ? (
          <span className="rig-chip rig-value rig-mono" style={{ fontSize: 9.5 }}>
            VALUE TIER · {d.valueTier.toUpperCase()}
          </span>
        ) : null}
        {d.status === "Active" ? (
          <span className="rig-chip rig-solid-teal rig-mono" style={{ fontSize: 9.5 }}>
            <span className="rig-led" />
            ACTIVE
          </span>
        ) : (
          <span className="rig-chip rig-mono" style={{ fontSize: 9.5 }}>
            DORMANT
          </span>
        )}
        {d.caseStudy ? (
          <span className="rig-chip rig-mono" style={{ fontSize: 9.5 }}>
            CASE STUDY · {d.caseStudy.name.toUpperCase()}
          </span>
        ) : d.caseStudyStatus === "pending" ? (
          <span className="rig-chip rig-mono" style={{ fontSize: 9.5, opacity: 0.72 }}>
            CASE STUDY PENDING
          </span>
        ) : (
          <span className="rig-chip rig-mono" style={{ fontSize: 9.5, opacity: 0.72 }}>
            NO CASE STUDY
          </span>
        )}
      </div>
      {d.caseStudy ? <div className="rig-del-snippet">{d.caseStudy.snippet}</div> : null}
    </div>
  );
}

function WarmCard({ warm }: { warm: RigWarmPath }): JSX.Element {
  return (
    <div className="rig-warm-card">
      <div className="rig-wp-eyebrow">Warm path in</div>
      <div className="rig-wp-line">
        <RichTextView value={warm.line} />
      </div>
      <button className="rig-btn rig-primary" type="button" disabled>
        {warm.cta} <span className="rig-soon">Coming soon</span>
      </button>
      <div className="rig-echo">{warm.echo}</div>
    </div>
  );
}

function ReadRow({
  contact,
  selected,
  onSelect,
}: {
  contact: RigContact;
  selected: boolean;
  onSelect: (id: string) => void;
}): JSX.Element {
  const ownerTxt = contact.owner ? `${contact.owner}'s contact` : "A colleague's contact";
  return (
    <button
      className={`rig-read-row${selected ? " rig-selected" : ""}`}
      type="button"
      onClick={() => onSelect(contact.id)}
    >
      <div className="rig-read-name">
        {contact.name}
        <span className="rig-ttl">{roleSuffix(contact.title, contact.company)}</span>
      </div>
      <div className="rig-read-basisline">
        <BasisChip basis={contact.basis} />
      </div>
      <div className="rig-read-foot">
        <span className="rig-read-prov">
          {contact.sourceGroup} · {ownerTxt} · {contact.touches} touches
          {contact.lastDays != null ? `, last ${contact.lastDays}d` : ""}
          {contact.alsoMatches > 0 ? ` · also matches ${contact.alsoMatches} other signals` : ""}
        </span>
        <TempPip temp={contact.temp} />
      </div>
    </button>
  );
}

function SelectedContact({
  contact,
  onOpenModal,
}: {
  contact: RigContact;
  onOpenModal: (id: string) => void;
}): JSX.Element {
  const owner = contact.owner ?? "A colleague";
  return (
    <>
      <div className="rig-basis-headline">
        <span className="rig-lead">{contact.name}</span>
        {roleSuffix(contact.title, contact.company)}
      </div>
      <div className="rig-kv">
        <b>Owner ·</b> {owner} &nbsp; <b>Source ·</b> {contact.sourceGroup} &nbsp; <b>Met ·</b>{" "}
        {contact.dateMet}
      </div>
      <div className="rig-kv">
        <b>Interactions ·</b> {contact.touches} touches
        {contact.lastDays != null ? `, last ${contact.lastDays}d` : ""}
        {contact.alsoMatches > 0 ? (
          <>
            {" "}
            &nbsp; <b>Also matches ·</b> {contact.alsoMatches} other signals
          </>
        ) : null}
      </div>
      <div className="rig-basis-headline" style={{ marginTop: 10 }}>
        <BasisChip basis={contact.basis} />
        <div style={{ marginTop: 6 }}>
          {contact.basis === "direct" ? (
            <>
              Matched as a <b>direct</b> tie — {contact.phrase}
            </>
          ) : (
            <>
              Matched as a <b>consortium participant</b> — {contact.phrase}
            </>
          )}
        </div>
      </div>
      <div className={`rig-temp-strip rig-${contact.temp}`} style={{ marginTop: 10 }}>
        TEMP · {TEMP_LABEL[contact.temp].toUpperCase()}
      </div>
      <div style={{ marginTop: 12 }}>
        {contact.active ? (
          <span className="rig-chip rig-solid-teal rig-mono">
            <span className="rig-led" />
            ACTIVE ENGAGEMENT
          </span>
        ) : (
          <span className="rig-chip rig-mono">ADJACENCY · NOT AN ACTIVE ENGAGEMENT</span>
        )}
      </div>
      <div style={{ marginTop: 12 }}>
        <button
          className="rig-btn rig-wide rig-ghost"
          type="button"
          onClick={() => onOpenModal(contact.id)}
        >
          Open contact ↗ — full record (email · phone · LinkedIn · notes)
        </button>
      </div>
    </>
  );
}

/** The overflow cluster row + inline expansion of its contacts into the reads. */
function ClusterReads({
  vm,
  selectedContactId,
  expandedClusters,
  onToggle,
  onSelect,
}: {
  vm: RigViewModel;
  selectedContactId: string;
  expandedClusters: Set<string>;
  onToggle: (basis: string) => void;
  onSelect: (id: string) => void;
}): JSX.Element {
  return (
    <>
      {vm.clusters.map((cl) => {
        const open = expandedClusters.has(cl.basis);
        return (
          <div key={cl.basis}>
            <div className="rig-cluster-row">
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <BasisChip basis={cl.basis} />
                <b>
                  +{cl.count} more{cl.company ? ` at ${cl.company}` : ""}
                </b>
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <span className="rig-agg">
                  {cl.basisMix} · {cl.tempBand}
                </span>
                <button
                  className="rig-mini-link"
                  type="button"
                  aria-expanded={open}
                  onClick={() => onToggle(cl.basis)}
                >
                  {open ? "Collapse ▴" : "Open to expand ↗"}
                </button>
              </div>
            </div>
            {/* Cluster expands with a spring (3B.1): the revealed contacts settle
                in as staged fade-ups via the shared rig-stagger cascade. */}
            {open ? (
              <div className="rig-stagger">
                {cl.contacts.map((c) => (
                  <ReadRow
                    key={c.id}
                    contact={c}
                    selected={c.id === selectedContactId}
                    onSelect={onSelect}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

export function RigSurface({
  vm,
  signalId,
}: {
  vm: RigViewModel;
  signalId: string | null;
}): JSX.Element {
  const graphModel = useMemo(() => layoutGraph(vm), [vm]);
  const signal = useMemo(() => signalNodeLabel(vm), [vm]);
  const helpTipId = useId();

  // Density suffix (amendment A3), derived from the rendered graph: "full" when
  // the graph carries directs, cross-practice deliveries, or an overflow cluster
  // (a developed account — the BlackRock example); "lean" when it is only a
  // participant fan through the waypoint (a net-new account — the QIA example).
  const graphDensity =
    graphModel.directs.length > 0 ||
    graphModel.deliveries.length > 0 ||
    graphModel.clusters.length > 0
      ? "full"
      : "lean";

  const firstContactId = vm.contacts[0]?.id ?? "";
  const [selectedContactId, setSelectedContactId] = useState(firstContactId);
  const [highlightNodeId, setHighlightNodeId] = useState<string | null>(firstContactId || null);
  const [activeOnly, setActiveOnly] = useState(false);
  const [modalContactId, setModalContactId] = useState<string | null>(null);
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());

  // The interpretation rail begins below the full-width graph hero, so selecting
  // a node in the graph brings the split (and its rail) into view. The scroll is
  // choreographed WITH the selection cascade (3B.2), not fired as a separate jump.
  const splitRef = useRef<HTMLDivElement>(null);

  // Selection + modal look across the shown contacts AND expanded cluster
  // contacts, so a revealed cluster contact selects and opens like any other.
  const allContacts = [...vm.contacts, ...vm.clusters.flatMap((c) => c.contacts)];
  const selectedContact =
    allContacts.find((c) => c.id === selectedContactId) ?? vm.contacts[0] ?? null;
  const modalContact = allContacts.find((c) => c.id === modalContactId) ?? null;

  // Full action history collapses to a single recent-activity whisper (the lead
  // ledger line); the expandable history block is gone in v2.
  const recentActivity = vm.activation.history[0] ?? null;

  function selectContact(id: string): void {
    setSelectedContactId(id);
    setHighlightNodeId(id);
  }

  function toggleCluster(basis: string): void {
    setExpandedClusters((prev) => {
      const next = new Set(prev);
      if (next.has(basis)) next.delete(basis);
      else next.add(basis);
      return next;
    });
  }

  function scrollSplitIntoView(): void {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    splitRef.current?.scrollIntoView({
      behavior: reduce ? "auto" : "smooth",
      block: "start",
    });
  }

  // R-6/D. One destination, one decision function, two affordances: the
  // "Back to signal" control above and a click on inert page background. Both
  // resolve through rigBackTarget, so they cannot drift apart.
  const navigate = useNavigate();
  const backTo = rigBackTarget(signalId);

  const returnToSignal = useCallback((): void => {
    if (backTo) navigate(backTo);
  }, [backTo, navigate]);

  // Bound to the page root, so isInertBackgroundClick's target===currentTarget
  // test fires only on the root's OWN box — its padding band and the gaps
  // between the entry card, the graph hero and the split. Every descendant, the
  // graph canvas above all, is excluded structurally rather than by a blocklist.
  // A browser fires `click` on the nearest common ancestor of mousedown and
  // mouseup, so a gesture starting inside the graph and releasing over the
  // background would otherwise arrive with target === the root. Record where the
  // press began and require it to have been the background too.
  const pressStartedOnBackground = useRef(false);

  const onBackgroundPointerDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      pressStartedOnBackground.current = event.target === event.currentTarget;
    },
    [],
  );

  const onBackgroundClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      if (!backTo) return;
      const selection = typeof window !== "undefined" ? window.getSelection() : null;
      const hasTextSelection = selection ? !selection.isCollapsed : false;
      const ok = isInertBackgroundClick(event, {
        hasTextSelection,
        pressStartedOnBackground: pressStartedOnBackground.current,
      });
      pressStartedOnBackground.current = false;
      if (!ok) return;
      returnToSignal();
    },
    [backTo, returnToSignal],
  );

  function onNode(node: GNode): void {
    if (node.kind === "contact") {
      // Choreograph the scroll-anchor with the selection cascade (3B.2): select
      // first, then defer the scroll to the next frame so it begins as the keyed
      // rail remounts and its staged fade-ups start against the settled layout —
      // one continuous sequence, not a jump followed by an animation. Read-row
      // clicks select without scrolling (the reads are already in view).
      selectContact(node.id);
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(scrollSplitIntoView);
      } else {
        scrollSplitIntoView();
      }
    } else {
      setHighlightNodeId(node.id);
    }
  }

  return (
    <div
      className="sx-rig"
      onMouseDown={onBackgroundPointerDown}
      onClick={onBackgroundClick}
    >
      <EntryCard vm={vm} backTo={backTo} />

      {/* FULL-WIDTH GRAPH HERO — the graph leads, above the split. Eyebrow +
          graph controls share the top row; the recent-activity whisper (the
          collapsed action history) sits beneath the canvas, per sealed IA-2. */}
      <div className="rig-graph-hero">
        <div className="rig-gh-top">
          <div className="rig-gh-left">
            <span className="rig-gh-eyebrow">Relationship graph · {graphDensity} density</span>
            {/* Persistent inline legend (amendment A2), matching the sealed mock's
                hero legend items, wording, and swatch treatment. The help tip
                (kept) carries the top-3 density ranking rule the legend omits. */}
            <div className="rig-graph-legend">
              <i>
                <span className="rig-lg-swatch" />
                direct
              </i>
              <i>
                <span className="rig-lg-swatch rig-dash" />
                participant · via waypoint
              </i>
              <i>
                <span className="rig-lg-dot rig-direct" />
                direct basis
              </i>
              <i>
                <span className="rig-lg-dot" />
                participant basis (orange)
              </i>
              <i>size = temperature</i>
            </div>
          </div>
          <div className="rig-graph-controls">
            <button
              className={`rig-toggle${activeOnly ? " rig-on" : ""}`}
              type="button"
              aria-pressed={activeOnly}
              onClick={() => setActiveOnly((v) => !v)}
            >
              <span className="rig-track">
                <span className="rig-knob" />
              </span>
              <span>Active engagements only</span>
            </button>
            <span className="rig-help-wrap">
              <button
                className="rig-help"
                type="button"
                aria-label="Graph legend and density"
                aria-describedby={helpTipId}
              >
                ?
              </button>
              <span className="rig-help-tip" role="tooltip" id={helpTipId}>
                <span>Solid = direct · dashed-via-waypoint = participant · curve = delivery</span>
                <span>Top 3 per org, ranked by strength → temperature → recency</span>
              </span>
            </span>
          </div>
        </div>
        <RigGraph
          model={graphModel}
          signal={signal}
          highlightId={highlightNodeId}
          dim={activeOnly}
          onNode={onNode}
        />
        {recentActivity ? (
          <div className="rig-whisper" aria-label="Recent activity">
            Recent · {recentActivity}
          </div>
        ) : null}
      </div>

      <section>
        <div className="rig-ia2grid" ref={splitRef}>
          {/* EVIDENCE COLUMN (left) — relationship reads, then cross-practice delivery. */}
          <div className="rig-stagger">
            <div className="rig-panel rig-pad">
              <div className="rig-sec-eyebrow">
                Relationship reads{" "}
                <span style={{ color: "var(--ink-40)" }}>· one story, per-contact basis</span>
              </div>
              {vm.contacts.some((c) => c.basis === "participant") ? (
                <div className="rig-reads-note">
                  These contacts participate in the consortium — they are not employees of the
                  signal entity.
                </div>
              ) : null}
              <div>
                {vm.contacts.map((c) => (
                  <ReadRow
                    key={c.id}
                    contact={c}
                    selected={c.id === selectedContactId}
                    onSelect={selectContact}
                  />
                ))}
                <ClusterReads
                  vm={vm}
                  selectedContactId={selectedContactId}
                  expandedClusters={expandedClusters}
                  onToggle={toggleCluster}
                  onSelect={selectContact}
                />
              </div>
            </div>

            <div className="rig-panel rig-pad">
              <div className="rig-sec-eyebrow">
                Cross-practice delivery{" "}
                <span style={{ color: "var(--ink-40)" }}>· sanitized · shared store</span>
              </div>
              {vm.deliveries.length > 0 ? (
                vm.deliveries.map((d, i) => <DeliveryRow key={i} d={d} />)
              ) : (
                <div className="rig-empty-state">
                  No cross-practice delivery on record for this account — a net-new relationship.
                </div>
              )}
            </div>
          </div>

          {/* INTERPRETATION RAIL (right, sticky) — selected contact → Pattern Read → warm paths. */}
          <div className="rig-rail-sticky rig-stagger">
            {selectedContact ? (
              <div className="rig-panel rig-pad">
                <div className="rig-sec-eyebrow">
                  Selected contact <span style={{ color: "var(--ink-40)" }}>· read detail</span>
                </div>
                {/* Keyed on the contact so selection re-cascades as staged fade-ups. */}
                <div className="rig-stagger" key={selectedContact.id}>
                  <SelectedContact contact={selectedContact} onOpenModal={setModalContactId} />
                </div>
              </div>
            ) : null}

            <PatternReadPanel reads={vm.patternReads} />

            <div className="rig-panel rig-pad">
              <div className="rig-sec-eyebrow">
                Warm paths{" "}
                <span style={{ color: "var(--ink-40)" }}>· sanitized · consultants name-only</span>
              </div>
              {vm.activation.warmPath ? (
                <WarmCard warm={vm.activation.warmPath} />
              ) : (
                <div className="rig-empty-state">
                  No warm path on record — a net-new relationship for the firm, with no prior
                  delivery to carry an introduction. The first outreach establishes the path.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <RigContactModal contact={modalContact} onClose={() => setModalContactId(null)} />
    </div>
  );
}
